import { z } from "zod";
import {
  ApplicationArea,
  ApplicationCountry,
  ApplicationInquiryRequest,
  DatacycleUse,
  InquiryCreateRequest,
  TeamSize,
} from "@ai-platform/contracts";
import type { InquiryGuard, Translate } from "./inquiry.ts";

/*
 * The application for the beta (ADR-143, addendum to ADR-093 and ADR-133 point 1):
 * what the form asks, checked with the contract (07 §5), its choices as the contract's codes and
 * the body for `POST /api/inquiries`. Portal and website open it in their inquiry dialog where
 * "Zugang anfragen" stood (TL, 2026-10-27, for the alpha release). The component
 * `ApplicationForm.vue` renders and sends; everything it decides lives here, so it is tested here.
 */

/** A text field the person may leave empty is sent without it, as the contract's optional field. */
const blankAsMissing = (value: unknown) => (typeof value === "string" && value.trim() === "" ? undefined : value);

/**
 * What the person fills in: the contract's body without what the form adds itself (`kind`,
 * honeypot, `startedAt`), an empty phone number or goal read as none.
 */
export const applicationValueSchema = ApplicationInquiryRequest.omit({
  kind: true,
  website: true,
  startedAt: true,
}).extend({
  phone: z.preprocess(blankAsMissing, ApplicationInquiryRequest.shape.phone),
  message: z.preprocess(blankAsMissing, ApplicationInquiryRequest.shape.message),
});

/** The fields of the form by their name in the body; a refused one is marked by it (`ApplicationForm`). */
export type ApplicationField = keyof z.input<typeof applicationValueSchema>;

/** The fixed choices as codes, in the order of the design prototype (`BF`); the labels are texts. */
export const applicationChoices = {
  country: ApplicationCountry.options,
  datacycleUse: DatacycleUse.options,
  teamSize: TeamSize.options,
  areas: ApplicationArea.options,
} as const;

export type ApplicationChoice = keyof typeof applicationChoices;

/**
 * The label of a code, the same in the form and in the staff area: the text
 * `applicationForm.<field>.option.<code>`, e.g. `IT` as "Italien (Südtirol)".
 */
export function applicationChoiceText(field: ApplicationChoice, code: string, t: Translate): string {
  return t(`applicationForm.${field}.option.${code}`);
}

/** The text key of a field's error, one per field as in the prototype, with the contract's limits. */
export function applicationErrorKey(field: string): string {
  return `applicationForm.${field}.error`;
}

/**
 * The heading the app puts above the form. After a login refused for an instance not enabled
 * (`instance_not_enabled`, ADR-093 point 8) the intro says why, as the inquiry's entry did.
 */
export function applicationHeading(t: Translate, lead?: "instance"): { title: string; intro: string } {
  return {
    title: t("applicationForm.title"),
    intro: t(lead ? `inquiryForm.lead.${lead}.intro` : "applicationForm.intro"),
  };
}

/**
 * The body for `POST /api/inquiries` from the checked values, or nothing when it does not hold
 * the contract (a filled honeypot, or a form sent before it was shown). Blank fields are left out.
 */
export function applicationBody(
  values: Record<string, unknown>,
  guard: InquiryGuard,
): InquiryCreateRequest | undefined {
  const filled = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && blankAsMissing(value) !== undefined),
  );
  const parsed = InquiryCreateRequest.safeParse({
    ...filled,
    kind: "application",
    website: guard.website,
    startedAt: guard.startedAt,
  });
  return parsed.success ? parsed.data : undefined;
}
